import {
  Box,
  Chip,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from '@tanstack/react-router';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { Scrollbar } from '../../../../components/atoms/scrollbar';
import { Status } from '../../../../components/atoms/status';
import AvatarWithInfo from '../../../../components/molecules/avatar-with-info';
import { DiscountHistory } from '../../../hosts/api/types/discount';

import { DataStatusIndicator } from '@/components/molecules/data-status-indicator/data-status-indicator';
import { ParkingTicketState } from '@/features/visits/api/types/visits';

interface Props {
  data: DiscountHistory[];
  error: string;
  isLoading: boolean;
}

export const DiscountHistoryTable = ({ data, error, isLoading }: Props) => {
  const { t } = useTranslation(['hosts', 'billingGroups']);

  const columns = [
    {
      id: 'host',
      label: t('hosts:title'),
    },
    {
      id: 'discountCode',
      label: t('billingGroups:tableHistory.code'),
    },
    {
      id: 'discountName',
      label: t('billingGroups:tableHistory.name'),
    },
    {
      id: 'parkingTicket',
      label: t('billingGroups:tableHistory.ticket'),
    },
    {
      id: 'parkingTicketStatus',
      label: t('billingGroups:tableHistory.ticketStatus'),
    },
    {
      id: 'createdAt',
      label: t('billingGroups:tableHistory.createdAt'),
    },
  ];

  const handleColorStatus = (status: ParkingTicketState) => {
    switch (status) {
      case 'assigned':
        return '#1b774d';
      case 'discounted':
        return '#736Be9';
      case 'discount_error':
        return '#ef6f6b';
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}>
      <Scrollbar>
        <Table sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell key={column.id}>{column.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map(discount => (
              <TableRow hover key={discount.id}>
                <TableCell>
                  <Link
                    color="primary"
                    underline="none"
                    component={RouterLink}
                    to={`/hosts/${discount.host.id}`}
                    variant="inherit">
                    <AvatarWithInfo
                      avatarText={`${discount.host.firstName.slice(
                        0,
                        1,
                      )} ${discount.host.lastName.slice(0, 1)}`}
                      primaryText={`${discount.host.firstName} ${discount.host.lastName}`}
                      secondaryText={discount.host.email}
                      src=""
                    />
                  </Link>
                </TableCell>
                <TableCell>{discount.discountCode}</TableCell>
                <TableCell>{discount.discountName}</TableCell>
                <TableCell>
                  <Chip
                    variant="outlined"
                    color="success"
                    label={discount.parkingTicket.ticketNumber}
                  />
                </TableCell>
                <TableCell>
                  <Status
                    color={handleColorStatus(discount?.parkingTicket?.status) || ''}
                    label={discount?.parkingTicket?.status}
                  />
                </TableCell>
                <TableCell>
                  <Typography color="inherit" variant="inherit">
                    {format(new Date(discount.createdAt), 'dd MMM yyyy')}
                  </Typography>
                  <Typography color="textSecondary" variant="inherit">
                    {format(new Date(discount.createdAt), 'HH:mm')}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Scrollbar>
      <DataStatusIndicator dataLength={data?.length} error={error} isFetching={isLoading} />
    </Box>
  );
};
